/**
 * Step 3 of the Production Line journey (production-lines): the ranked
 * directions the analysis produced, one card each, the local selection and
 * the "Create my video" bar. Moved verbatim out of
 * ProductionLineDetailPage.tsx.
 */

import { useId, type ReactNode } from 'react';

import type { MerchantProfileView } from '../../merchant/api.js';
import type { WorkspaceCopy } from '../../merchant/copy.js';
import { localizeMerchantFacingText } from '../../merchant/shared/merchantFacingText.js';
import {
  Button,
  Callout,
  CheckIcon,
  Eyebrow,
  Pix,
  SoonBadge,
  Tag,
  WarningIcon,
} from '../../merchant/ui/primitives.js';
import { basisLabel, isContentIdea } from './RecommendationReferences.js';
import { ResearchReferenceList } from './ResearchReferenceList.js';
import { contentLanguage, researchUi } from './researchPresentation.js';

type Recommendation = NonNullable<
  MerchantProfileView['result']
>['recommendations'][number];

export function RecommendationsStep({
  copy,
  language,
  workspaceName,
  view,
  selected,
  busy,
  onSelect,
  onCreate,
  readOnly = false,
}: {
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly workspaceName: string;
  readonly view: MerchantProfileView;
  readonly selected: string | null;
  readonly busy: boolean;
  readonly onSelect: (id: string) => void;
  readonly onCreate: () => void;
  readonly readOnly?: boolean;
}): ReactNode {
  const contentResearch = view.result?.strategy === 'CONTENT_RESEARCH_V1';
  const ui = researchUi[language];
  const recommendations = view.result?.recommendations ?? [];
  const unavailable =
    view.result?.handoffArtifactId === null ||
    view.result?.handoffArtifactId === undefined;
  const chosen = recommendations.find((item) => item.artifactId === selected) ?? null;
  return (
    <section aria-labelledby="rc-h">
      <Eyebrow>
        <Pix status="ready-to-choose" />
        {contentResearch
          ? language === 'ar'
            ? 'الفكرة والنص'
            : 'Idea & script'
          : copy.line.step3}
      </Eyebrow>
      <h2 id="rc-h" className="mj-ws-h2 mj-ws-mt-8">
        {copy.line.recsTitle(workspaceName)}
      </h2>
      <p className="mj-ws-muted mj-ws-sm" style={{ marginTop: 4, maxWidth: '60ch' }}>
        {readOnly
          ? ui.readOnly
          : contentResearch
            ? ui.selectionNote
            : copy.line.recsBody}
      </p>
      {!contentResearch ? (
        <div className="mj-ws-row mj-ws-mt-12">
          <Tag>{copy.line.neutralTaste}</Tag>
          <SoonBadge />
        </div>
      ) : null}
      {unavailable ? (
        <Callout className="mj-ws-mt-20">
          <WarningIcon />
          <span className="mj-ws-callout-copy mj-ws-sm">
            {copy.line.recsUnavailable}
          </span>
        </Callout>
      ) : (
        <div className="mj-ws-g3 mj-ws-mt-20" style={{ alignItems: 'stretch' }}>
          {recommendations.map((recommendation, index) => (
            <RecommendationCard
              key={recommendation.artifactId}
              recommendation={recommendation}
              // No approved ranking rule exists for content ideas: no badge.
              recommended={index === 0 && !isContentIdea(recommendation)}
              selected={recommendation.artifactId === selected}
              copy={copy}
              language={language}
              onSelect={() => {
                if (!readOnly) onSelect(recommendation.artifactId);
              }}
              readOnly={readOnly}
            />
          ))}
        </div>
      )}
      {contentResearch && !unavailable ? (
        <ResearchReferenceList
          ideas={recommendations}
          copy={copy}
          language={language}
        />
      ) : null}
      {readOnly ? null : (
        <div className="mj-ws-stickybar">
          <span className="mj-ws-muted mj-ws-sm">
            {chosen === null
              ? copy.line.selectHint
              : copy.line.selectedHint(chosen.title)}
          </span>
          <Button
            variant="primary"
            pixelWave={contentResearch}
            aria-busy={busy}
            disabled={chosen === null || busy || unavailable}
            onClick={onCreate}
          >
            {contentResearch
              ? busy
                ? ui.preparing
                : ui.prepare
              : busy
                ? copy.line.creating
                : copy.line.createVideo}
          </Button>
        </div>
      )}
    </section>
  );
}

/** The content research v1 body: angle, the research surfaces and the evidence note. */
function ContentIdeaBody({
  recommendation,
  copy,
  language,
}: {
  readonly recommendation: Recommendation;
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
}): ReactNode {
  return (
    <>
      <dl className="mj-ws-rdl">
        <dt>{copy.line.angle}</dt>
        <dd dir="auto">{recommendation.angle}</dd>
        <dt>{copy.line.hook}</dt>
        <dd dir="auto">{recommendation.hook ?? researchUi[language].unavailable}</dd>
        <dt>{copy.line.supportingBasis}</dt>
        <dd dir="auto">
          {recommendation.creativeTreatment ?? copy.line.supportingBasisUnavailable}
        </dd>
      </dl>
      <details className="mj-ws-details mj-ws-sm">
        <summary>{copy.line.whyFits}</summary>
        <p dir="auto">{recommendation.whyRelevant ?? copy.line.whyFitsUnavailable}</p>
        {(recommendation.references ?? []).length === 0 ? (
          <p className="mj-ws-muted">
            {recommendation.basis === 'BUSINESS_BASED'
              ? copy.line.noReferences
              : copy.line.referencesUnavailable}
          </p>
        ) : (
          <p className="mj-ws-muted">
            {researchUi[language].references}:{' '}
            {(recommendation.references ?? [])
              .map((ref) =>
                ref.creatorHandle === null ? ref.platform : `@${ref.creatorHandle}`,
              )
              .join(' · ')}
          </p>
        )}
        {recommendation.limitations[0] !== undefined ? (
          <p className="mj-ws-muted mj-ws-sm mj-ws-mt-8">
            <strong>{copy.line.evidenceNote}</strong>{' '}
            {localizeMerchantFacingText(recommendation.limitations[0], language)}
          </p>
        ) : null}
      </details>
    </>
  );
}

function RecommendationCard({
  recommendation,
  recommended,
  selected,
  copy,
  language,
  onSelect,
  readOnly,
}: {
  readonly recommendation: Recommendation;
  readonly recommended: boolean;
  readonly selected: boolean;
  readonly copy: WorkspaceCopy;
  readonly language: 'en' | 'ar';
  readonly onSelect: () => void;
  readonly readOnly: boolean;
}): ReactNode {
  const headingId = useId();
  const unsuitable = recommendation.povExecutability?.status === 'NOT_POV_SUITABLE';
  const disabled = readOnly || unsuitable;
  return (
    <article
      className={`mj-ws-rec${disabled ? ' is-disabled' : ''}`}
      data-selected={selected}
      aria-labelledby={headingId}
      onClick={(event) => {
        const target = event.target;
        if (
          !disabled &&
          target instanceof Element &&
          target.closest('button,a,summary,details') === null
        )
          onSelect();
      }}
    >
      <div className="mj-ws-rec-head mj-ws-rec-basis">
        {recommendation.basis ? (
          <Tag>{basisLabel(recommendation.basis, copy)}</Tag>
        ) : null}
        {recommended ? <Tag accent>{copy.line.recommended}</Tag> : null}
        <span className="mj-ws-check" aria-hidden="true">
          <CheckIcon />
        </span>
      </div>
      <h3 id={headingId} className="mj-ws-h3" dir="auto">
        {recommendation.title}
      </h3>
      <p className="mj-ws-sm" dir="auto">
        {recommendation.objective}
      </p>
      {isContentIdea(recommendation) ? (
        <p className="mj-ws-muted mj-ws-xs">
          {researchUi[language].content}:{' '}
          {contentLanguage(recommendation.outputLocale, language)}
        </p>
      ) : null}
      {isContentIdea(recommendation) ? (
        <ContentIdeaBody
          recommendation={recommendation}
          copy={copy}
          language={language}
        />
      ) : (
        <dl className="mj-ws-rdl">
          <dt>{copy.line.angle}</dt>
          <dd>{recommendation.angle}</dd>
          <dt>{copy.line.audience}</dt>
          <dd>{recommendation.targetAudienceOrNeed.join(' · ')}</dd>
          <dt>{copy.line.proof}</dt>
          <dd>{recommendation.proofStructure}</dd>
          {recommendation.ctaDirection !== null ? (
            <>
              <dt>{copy.line.cta}</dt>
              <dd>{recommendation.ctaDirection}</dd>
            </>
          ) : null}
        </dl>
      )}
      {isContentIdea(recommendation) ? null : (
        <details className="mj-ws-details mj-ws-sm">
          <summary>{copy.line.whyFits}</summary>
          <p className="mj-ws-mt-8">
            {recommendation.whyItFits.join(' · ') || recommendation.rankingRationale}
          </p>
          {recommendation.limitations[0] !== undefined ? (
            <p className="mj-ws-muted mj-ws-mt-8">
              <strong>{copy.line.evidenceNote}</strong>{' '}
              {localizeMerchantFacingText(recommendation.limitations[0], language)}
            </p>
          ) : null}
        </details>
      )}
      {unsuitable ? (
        <p className="mj-ws-muted mj-ws-sm">
          <strong>{copy.line.notSuitable}</strong>{' '}
          {recommendation.povExecutability.reason}
        </p>
      ) : null}
      {readOnly ? null : (
        <Button
          variant={selected ? 'primary' : 'secondary'}
          small
          className="mj-ws-rec-action"
          aria-pressed={selected}
          disabled={disabled}
          onClick={onSelect}
        >
          {selected ? copy.line.selected : copy.line.select}
        </Button>
      )}
    </article>
  );
}
